import { useMemo, useRef } from "react";
import { Layout } from "@/components/layout/layout";
import { useListVenues } from "@workspace/api-client-react";
import { useSeo } from "@/lib/seo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, Users, Car } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { CardGridSkeleton, ListEmpty, ListErrorBlock } from "@/components/list-state";
import { VenuesMap, type VenueMapPin, type VenuesMapApi } from "@/components/venues-map";

export default function Venues() {
  useSeo({
    title: "Pub Poker Venues",
    description:
      "Find pubs and clubs running Interclub Poker Circuit tournaments. Browse venues, schedules, and recurring weekly poker nights.",
    canonicalPath: "/venues",
  });
  const { data: venues, isLoading, isError, error, refetch } = useListVenues();
  const mapApiRef = useRef<VenuesMapApi | null>(null);

  const withCoords = useMemo<VenueMapPin[]>(() => {
    if (!venues) return [];
    return venues
      .filter((v) => typeof v.latitude === "number" && typeof v.longitude === "number")
      .map((v) => ({
        id: v.id,
        name: v.name,
        address: v.address,
        city: v.city,
        state: v.state,
        latitude: v.latitude as number,
        longitude: v.longitude as number,
      }));
  }, [venues]);

  const withoutCoordsCount = useMemo(
    () =>
      (venues ?? []).filter(
        (v) => !(typeof v.latitude === "number" && typeof v.longitude === "number"),
      ).length,
    [venues],
  );

  const basePath = import.meta.env.BASE_URL;

  return (
    <Layout>
      <div className="bg-card border-b border-border py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-foreground tracking-tight">Circuit Venues</h1>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
            The best pubs and clubs hosting our tournaments. Great food, cold taps, and dedicated poker spaces.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
        {isLoading ? (
          <CardGridSkeleton count={6} />
        ) : isError ? (
          <ListErrorBlock error={error} onRetry={() => refetch()} title="Couldn't load venues" />
        ) : venues?.length === 0 ? (
          <ListEmpty
            icon={<MapPin className="h-7 w-7" />}
            title="No venues listed yet"
            description="Circuit venues will appear here once they're added."
          />
        ) : (
          <>
            {withCoords.length > 0 && (
              <Card className="overflow-hidden bg-background border-border">
                <div className="aspect-[16/9] sm:aspect-[21/9] w-full bg-muted">
                  <VenuesMap
                    venues={withCoords}
                    basePath={basePath}
                    className="w-full h-full"
                    onReady={(api) => {
                      mapApiRef.current = api;
                    }}
                  />
                </div>
                <CardContent className="p-3 text-xs text-muted-foreground">
                  Showing {withCoords.length} venue{withCoords.length === 1 ? "" : "s"} on the map. Click a pin or use "Show on map" to zoom in.
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {venues?.map((venue) => {
                const hasCoords =
                  typeof venue.latitude === "number" && typeof venue.longitude === "number";
                return (
                  <Link key={venue.id} href={`/venues/${venue.id}`}>
                    <Card className="h-full bg-background border-border hover:border-primary/50 transition-colors cursor-pointer group">
                      <CardHeader>
                        <CardTitle className="text-xl group-hover:text-primary transition-colors">
                          {venue.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-start text-muted-foreground">
                          <MapPin className="mr-2 h-5 w-5 text-primary/70 shrink-0" />
                          <span className="text-sm">
                            {venue.address}<br />
                            {venue.city}, {venue.state}
                          </span>
                        </div>
                        {venue.capacity && (
                          <div className="flex items-center text-muted-foreground text-sm">
                            <Users className="mr-2 h-4 w-4 text-primary/70" />
                            Capacity: {venue.capacity} players
                          </div>
                        )}
                        {venue.parkingNotes && (
                          <div
                            className="flex items-start text-muted-foreground text-sm border-t border-border/50 pt-3"
                            data-testid={`venue-parking-${venue.id}`}
                          >
                            <Car className="mr-2 h-4 w-4 text-primary/70 shrink-0 mt-0.5" />
                            <span className="line-clamp-2">{venue.parkingNotes}</span>
                          </div>
                        )}
                        {hasCoords && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              mapApiRef.current?.focusVenue(venue.id);
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                          >
                            <MapPin className="mr-2 h-4 w-4" /> Show on map
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>

            {withoutCoordsCount > 0 && withCoords.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {withoutCoordsCount} venue{withoutCoordsCount === 1 ? "" : "s"} not yet placed on the map.
              </p>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
